import { NuqsAdapter } from "nuqs/adapters/react-router/v8";
import { lazy, type ReactNode, Suspense } from "react";
import { BrowserRouter, type Location, Navigate, Route, Routes, useLocation } from "react-router";
import { ApiError } from "./api";
import { useSession } from "./auth";
import { DashboardLayout } from "./components/dashboard-layout";
import { IssueDetailModal } from "./components/issues/issue-modal";
import { FullPageSpinner, SignIn, Unreachable } from "./components/sign-in";
import { useIsMobile } from "./hooks/use-mobile";
import { backgroundLocationOf } from "./lib/issue-modal";
import { useIsAdmin, useIsManager, useIsMember, useMe } from "./lib/permissions";
import { AgentsPage } from "./pages/AgentsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { InboxPage } from "./pages/InboxPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { IssueDetailPage } from "./pages/IssueDetailPage";
import { IssuesPage } from "./pages/IssuesPage";
import { NewIssuePage } from "./pages/NewIssuePage";
import { OverviewPage } from "./pages/OverviewPage";
import { PreviewDetailPage } from "./pages/PreviewDetailPage";
import { PreviewsPage } from "./pages/PreviewsPage";
import { ReleaseDetailPage } from "./pages/ReleaseDetailPage";
import { ReleasesPage } from "./pages/ReleasesPage";
import { RepoDetailPage } from "./pages/RepoDetailPage";
import { ReposPage } from "./pages/ReposPage";
import { RunsPage } from "./pages/RunsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { TasksPage } from "./pages/TasksPage";
import { TeamDetailPage } from "./pages/TeamDetailPage";
import { TeamsPage } from "./pages/TeamsPage";
import { FactoryProvider } from "./store";

const TvPage = lazy(() => import("./pages/TvPage").then((m) => ({ default: m.TvPage })));
const SpritesPage = lazy(() => import("./pages/SpritesPage").then((m) => ({ default: m.SpritesPage })));
const EasterEggsPage = lazy(() =>
  import("./pages/EasterEggsPage").then((m) => ({ default: m.EasterEggsPage })),
);

export function App() {
  // The router reads the URL once, when it mounts. Behind the gate it mounts after the sign-in
  // callback has put back the link that was followed, rather than on `/auth/callback`.
  return (
    <Gate>
      <BrowserRouter>
        {/* Not shallow: React Router has to see every URL change, or `useLocation` and `NavLink` go stale. */}
        <NuqsAdapter defaultOptions={{ shallow: false }}>
          <FactoryProvider>
            <Suspense fallback={<FullPageSpinner label="Loading" />}>
              <AppRoutes />
            </Suspense>
          </FactoryProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </Gate>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isMobile = useIsMobile();
  // A phone follows the issue URL itself. The modal only covers the list on a wider screen.
  const background = isMobile ? undefined : backgroundLocationOf(location.state);
  return (
    <>
      <PageRoutes location={background ?? location} />
      {background && (
        <Routes>
          <Route path="/issues/:identifier" element={<IssueDetailModal background={background} />} />
        </Routes>
      )}
    </>
  );
}

/** With an issue modal open, `location` is the page under it rather than the URL. */
function PageRoutes({ location }: { location: Location }) {
  const admin = useIsAdmin();
  const manager = useIsManager();
  const member = useIsMember();
  return (
    <Routes location={location}>
      <Route path="/tv" element={<TvPage />} />
      {admin && <Route path="/sprites" element={<SpritesPage />} />}
      {admin && <Route path="/easter-eggs" element={<EasterEggsPage />} />}
      <Route element={<DashboardLayout />}>
        <Route index element={admin ? <OverviewPage /> : <Navigate to="/inbox" replace />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/issues" element={<IssuesPage />} />
        {member && <Route path="/issues/new" element={<NewIssuePage />} />}
        <Route path="/issues/:identifier" element={<IssueDetailPage />} />
        <Route path="/previews/:id" element={<PreviewDetailPage />} />
        {member && (
          <>
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:key" element={<TeamDetailPage />} />
            <Route path="/repos" element={<ReposPage />} />
            <Route path="/repos/:key" element={<RepoDetailPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
          </>
        )}
        {manager && (
          <>
            <Route path="/releases" element={<ReleasesPage />} />
            <Route path="/releases/:id" element={<ReleaseDetailPage />} />
            <Route path="/previews" element={<PreviewsPage />} />
          </>
        )}
        {admin && (
          <>
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:id" element={<TaskDetailPage />} />
            <Route path="/runs" element={<RunsPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </>
        )}
      </Route>
      <Route path="*" element={<Navigate to={admin ? "/" : "/inbox"} replace />} />
    </Routes>
  );
}

function Gate({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!session.ready) return <FullPageSpinner label="Checking your session" />;
  if (!session.authenticated) return <SignIn onSignIn={session.signIn} />;
  return <RoleGate>{children}</RoleGate>;
}

/** Waits for the role so the first paint never shows admin-only screens to a user. */
function RoleGate({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.data) return <>{children}</>;
  // A role check that never reached the API goes back to pending on every retry, so look at the
  // failure itself. An HTTP error did reach it and falls through as before.
  const failure = me.error ?? me.failureReason;
  if (me.fetchStatus === "paused" || (failure && !(failure instanceof ApiError))) {
    return <Unreachable retrying={me.fetchStatus === "fetching"} onRetry={() => void me.refetch()} />;
  }
  if (me.isPending) return <FullPageSpinner label="Checking your access" />;
  return <>{children}</>;
}
